import React, { FC, useEffect, useMemo } from 'react';
import BrochureCard from './components/brochureCard';
import { useLocation, useNavigate } from 'react-router';
import { updateSearchResults } from '../../hooks/useQueryParams.effect';
import { QueryParamsConst } from '../../constants/query-params.constants';
import Pagination from '../@shared/pagination';
import DesktopFilter from './components/desktopFilter';
import { DownloadBrochureListingModel } from './models/downloadBrochureListing.model';
import MobileFilter from './components/mobileFilter';
import DictionaryTerm from '../@shared/dictionaryTerm';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-react';
import { documentService, windowService } from '../../utils/window.utils';
import useMediaQuery from '../../hooks/useMediaQuery';
import { BreakpointsInPx } from '../../constants/breakpoints.constants';
import { TDownloadBrochureListingFilters, TDownloadBrochuresProps } from './types/downloadBrochures.types';
import './styles.scss';

const ProductDownloadBrochureListing: FC<TDownloadBrochuresProps> = (props) => {
  const model = new DownloadBrochureListingModel(props);

  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['tbl-p']})`);
  const filterQueryString = location?.search;
  const filterParams = useMemo(() => new URLSearchParams(location?.search), [filterQueryString]);
  const categories = filterParams.get(QueryParamsConst.CATEGORIES) || '';
  const line = filterParams.get(QueryParamsConst.LINE) || '';
  const series = filterParams.get(QueryParamsConst.SERIES) || '';
  const searchText = filterParams.get(QueryParamsConst.SEARCH_BROCHURE) || '';
  const selectedPage = filterParams.get(QueryParamsConst.PAGE) || '1';
  const selectedPageNo = parseInt(selectedPage);
  const noOfCardsPerPage = 12;
  const { CATEGORIES, LINE, SERIES, SEARCH_BROCHURE } = QueryParamsConst;
  const selectedFilters: TDownloadBrochureListingFilters = {
    [CATEGORIES]: categories,
    [LINE]: line,
    [SERIES]: series,
    [SEARCH_BROCHURE]: searchText,
  };

  const totalPageCount = Math.ceil(model.totalNumberOfCards / noOfCardsPerPage);

  const apiResponse = isEmptyResponse(props);
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);

  useEffect(() => {
    setTimeout(() => {
      const shareScript = 'https://static.addtoany.com/menu/page.js';
      const addthisScript = documentService()?.createElement?.('script');
      addthisScript.setAttribute('src', shareScript);
      const shareContentScript = documentService()?.querySelector?.(`[src="${shareScript}"]`);
      if (documentService()?.body && !shareContentScript) documentService()?.body?.appendChild?.(addthisScript);
      else windowService()?.a2a?.init_all?.();
    });
  }, [model.contextItemUrl, selectedFilters, selectedPage]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return (
      <ExperienceEditorComponentPlaceholder image={props.fields?.itemData?.thumbnail} componentName={props?.rendering?.componentName} />
    );
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={props?.rendering?.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  const onPaginationChange = (selectedPage: number): void => {
    updateSearchResults(navigate, { [QueryParamsConst.PAGE]: selectedPage.toString() });
  };

  return (
    <section>
      <div className={'product-download-brochure-listing-container product-download-brochure-listing-container__min-width-none'}>
        {isDesktop ? (
          <>
            <div className="product-download-brochure-listing-container__label-container">
              <span className="product-download-brochure-listing-container__label-container--title">
                <DictionaryTerm keyword="cnhi-download-brochure-listing-title" /> ({model.totalNumberOfCards})
              </span>
            </div>
            <DesktopFilter model={model} selectedFilters={selectedFilters} />
          </>
        ) : (
          <MobileFilter model={model} selectedFilters={selectedFilters} />
        )}
        <div className="product-download-brochure-listing-container__card-container">
          {model.brochureCards.map((card) => (
            <BrochureCard {...card} key={card.id} icon={model.icon} />
          ))}
        </div>
        <Pagination currentPage={selectedPageNo} pages={totalPageCount} goToPage={onPaginationChange} />
      </div>
    </section>
  );
};

export default ProductDownloadBrochureListing;
